import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmployeeAdminService,
  EmployeeResponse,
} from '@nebalkon-workspace/admin-api';
import { ProfileService } from '@nebalkon-workspace/auth';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { printStaffEmployeeNameBadge } from '../utils/staff-employee-name-badge-print.util';
import { formatEmployeeBadgeName } from '../utils/staff-employee-badge-name.util';

@Component({
  selector: 'lib-staff-employee-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FormsModule],
  templateUrl: './staff-employee-detail.component.html',
  styleUrl: './staff-employee-detail.component.scss',
})
export class StaffEmployeeDetailComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #employeeService = inject(EmployeeAdminService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);
  readonly #profileService = inject(ProfileService);
  readonly #printer = inject(NiimbotPrinterService);

  protected readonly mayPrintNameBadge = this.#printer.mayUseNiimbotPrinter;
  protected readonly printingNameBadge = signal(false);

  protected readonly employee = signal<EmployeeResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly errorKind = signal<'load' | 'notFound' | null>(null);
  protected readonly isToggling = signal(false);

  public ngOnInit(): void {
    const employeeId = this.#route.snapshot.paramMap.get('id');
    if (employeeId) {
      this.#loadEmployee(employeeId);
    } else {
      this.errorKind.set('notFound');
      this.loading.set(false);
    }
  }

  #loadEmployee(id: string): void {
    this.loading.set(true);
    this.errorKind.set(null);

    this.#employeeService.getEmployee(id).subscribe({
      next: (employee) => {
        this.employee.set(employee);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading employee:', err);
        this.errorKind.set('load');
        this.loading.set(false);
      },
    });
  }

  protected async onToggleActive(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const newActiveState = target.checked;
    const emp = this.employee();

    if (!emp || this.isToggling()) {
      target.checked = !newActiveState;
      return;
    }

    if (!newActiveState && emp.user.id === this.#profileService.userInfo()?.user.id) {
      target.checked = true;
      return;
    }

    this.isToggling.set(true);

    try {
      if (newActiveState) {
        await firstValueFrom(this.#employeeService.activateEmployee(emp.user.id));
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.employees.activateSuccess')],
        });
      } else {
        await firstValueFrom(
          this.#employeeService.deactivateEmployee(emp.user.id),
        );
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.employees.deactivateSuccess')],
        });
      }

      this.employee.set({
        ...emp,
        is_active: newActiveState,
      });
    } catch (error) {
      console.error('Error toggling employee status:', error);
      target.checked = !newActiveState;
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            newActiveState
              ? 'admin.employees.activateError'
              : 'admin.employees.deactivateError',
          ),
        ],
      });
    } finally {
      this.isToggling.set(false);
    }
  }

  protected onEdit(): void {
    const employeeId = this.#route.snapshot.paramMap.get('id');
    if (employeeId) {
      void this.#router.navigate([
        this.#cabinetPaths.staff,
        'employees',
        employeeId,
        'edit',
      ]);
    }
  }

  protected async onPrintNameBadge(): Promise<void> {
    const emp = this.employee();
    if (!emp || this.printingNameBadge()) {
      return;
    }

    this.printingNameBadge.set(true);
    try {
      await printStaffEmployeeNameBadge({
        position: emp.position,
        name: formatEmployeeBadgeName(emp),
        printer: this.#printer,
        translate: this.#translate,
        notification: this.#notification,
      });
    } finally {
      this.printingNameBadge.set(false);
    }
  }

  protected formatName(employee: EmployeeResponse): string {
    const ind = employee.individual;
    const parts = [ind.last_name, ind.first_name, ind.patronymic].filter(Boolean);
    return parts.join(' ') || employee.user.phone;
  }

  protected async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }
}
